import { Component, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthenticationResult } from '@azure/msal-browser';
import { filter, Subscription } from 'rxjs';

import { BUILD_INFO } from '../../environments/build-info';
import { environment } from '../../environments/environment';
import { AuthService, RoleService } from '../auth';
import { SettingsDrawerService } from '../settings/settings-drawer.service';

import { NavBarItemComponent } from './nav-bar-item/nav-bar-item.component';
import { NAV_BAR_CONFIG, NavBarConfig, NavBarItemType } from './nav-bar.config';

@Component({
	selector: 'df-nav-bar',
	imports: [NavBarItemComponent],
	templateUrl: './nav-bar.component.html',
	styleUrl: './nav-bar.component.scss'
})
export class NavBarComponent {
	NAV_BAR_CONFIG = NAV_BAR_CONFIG;
	subs: Subscription[] = [];
	user: AuthenticationResult | null = null;
	userInitials: string = '';
	visibleNavItems: NavBarConfig[] = [];
	isAuthResolved = false;
	currentUrl = '';
	isNavOpen = false;

	// Below 992px the collapsed panel is FLAT (shell handoff row 7): a dropdown parent renders as a
	// mono uppercase group label with its children listed beneath it, no toggle and no nested menu.
	// That is a different DOM shape, not a restyle, so the item template branches on this rather
	// than rendering both trees — duplicating every nav link would double it in the a11y tree.
	isMobileNav = signal(this.computeIsMobileNav());
	private mobileNavMql: MediaQueryList | null = null;
	private readonly onMobileNavMqlChange = () => this.isMobileNav.set(this.computeIsMobileNav());

	private computeIsMobileNav(): boolean {
		return typeof window !== 'undefined' && window.matchMedia('(width < 992px)').matches;
	}

	envIndicator =
		environment.demeterEnv != 'prod' ? `${environment.demeterEnv.toUpperCase()}` : '';

	buildBranchTitle =
		environment.demeterEnv != 'prod' ? BUILD_INFO.branch.replace(/^feature\//, '') : '';
	buildBranch =
		this.buildBranchTitle.length > 15
			? `${this.buildBranchTitle.slice(0, 14).replace(/-$/, '')}…`
			: this.buildBranchTitle;
	buildBranchUrl =
		BUILD_INFO.repoUrl && BUILD_INFO.branch
			? `${BUILD_INFO.repoUrl}/tree/${BUILD_INFO.branch}`
			: '';

	/** envIndicator is uppercased while buildBranch is not, so an exact compare never fires. */
	get showBranch(): boolean {
		return (
			!!this.buildBranch && this.buildBranch.toLowerCase() !== this.envIndicator.toLowerCase()
		);
	}

	private readonly settingsDrawer = inject(SettingsDrawerService);

	get userEmail(): string {
		return this.user?.account.username ?? '';
	}
	get userName(): string {
		return this.user?.account.name ?? '';
	}

	openSettings(): void {
		this.closeNav();
		this.settingsDrawer.openDrawer();
	}

	onItemAction(action: string): void {
		if (action === 'settings') this.openSettings();
	}

	constructor(
		private authService: AuthService,
		private roleService: RoleService,
		private router: Router
	) {}

	ngOnInit(): void {
		if (typeof window !== 'undefined') {
			this.mobileNavMql = window.matchMedia('(width < 992px)');
			this.mobileNavMql.addEventListener('change', this.onMobileNavMqlChange);
		}

		this.subs.push(
			this.authService.status$.subscribe(status => {
				// 'failed' must not read as resolved: this.user stays null in that status, and
				// resolved+no-user renders SIGN IN — which would sit beside the Tier 1 card's own
				// "Sign in again" (spec §5.4/§7 — the card, not the navbar, owns recovery here).
				this.isAuthResolved = status !== 'initializing' && status !== 'failed';
			}),
			this.authService.user$.subscribe(user => {
				this.user = user;

				this.userInitials =
					user?.account.name
						?.split(' ')
						.map((n: string) => n[0])
						.join('') || '';

				// Filter nav items based on user roles
				this.updateVisibleNavItems();
			}),
			this.router.events
				.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
				.subscribe(e => {
					this.currentUrl = e.urlAfterRedirects;
					this.closeNav();
				})
		);
	}

	/**
	 * The mobile panel's one rule, between the group-labelled rows and the flat ones (prototype
	 * `.sh-panel__sep`). Fires on the first BASE item that follows a DROPDOWN, so it disappears on
	 * its own for a role whose visible items are all one kind.
	 */
	showGroupSeparatorBefore(index: number): boolean {
		const items = this.visibleNavItems;
		if (index === 0) return false;
		const cur = items[index].type;
		const prev = items[index - 1].type;
		return (
			(cur === NavBarItemType.BASE && prev === NavBarItemType.DROPDOWN) ||
			(cur === NavBarItemType.ACTION && prev !== NavBarItemType.ACTION)
		);
	}

	/** A dropdown parent's own route is '#', so match any child's route prefix instead. */
	isItemActive(item: NavBarConfig): boolean {
		const routes = item.children?.length
			? item.children.map(c => c.route).filter((r): r is string => !!r)
			: [item.route].filter((r): r is string => !!r && r !== '#');
		return routes.some(r => this.currentUrl === r || this.currentUrl.startsWith(`${r}/`));
	}

	private _collapse(): any {
		const el = document.getElementById('navbar-main');
		return el
			? (window as any).bootstrap?.Collapse?.getOrCreateInstance(el, { toggle: false })
			: null;
	}

	toggleNav(): void {
		const c = this._collapse();
		if (!c) {
			return;
		}
		if (this.isNavOpen) {
			c.hide();
		} else {
			c.show();
		}
		this.isNavOpen = !this.isNavOpen;
	}

	closeNav(): void {
		if (this.isNavOpen) {
			this._collapse()?.hide();
			this.isNavOpen = false;
		}
	}

	/**
	 * Filter navigation items based on user roles
	 */
	private updateVisibleNavItems(): void {
		if (!this.user) {
			this.visibleNavItems = [];
			return;
		}

		this.visibleNavItems = NAV_BAR_CONFIG.filter(item => {
			// If no roles required, show to all authenticated users
			if (!item.requiredRoles || item.requiredRoles.length === 0) {
				return true;
			}

			// Check if user has at least one of the required roles
			return this.roleService.hasAnyRole(item.requiredRoles);
		})
			.map(item =>
				item.children
					? {
							...item,
							children: item.children.filter(
								child =>
									!child.requiredRoles?.length ||
									this.roleService.hasAnyRole(child.requiredRoles)
							)
						}
					: item
			)
			// A DROPDOWN whose children were all role-filtered would render a live toggle over an
			// empty menu (and a bare group label on the phone panel).
			.filter(
				item => item.type !== NavBarItemType.DROPDOWN || (item.children?.length ?? 0) > 0
			);
	}

	onLogout(): void {
		this.subs.push(this.authService.logout().subscribe());
	}

	onLogin(): void {
		this.subs.push(
			this.authService.login().subscribe(response => {
				console.log('Login response: ', response);
			})
		);
	}

	ngOnDestroy(): void {
		this.mobileNavMql?.removeEventListener('change', this.onMobileNavMqlChange);
		this.subs.forEach(s => s.unsubscribe());
		this.subs = [];
	}
}
